/**
 * Phase 2 admin React Query hook family. Every Wave-3 admin page imports
 * its data layer from here; no page should talk to apiFetch directly for
 * admin endpoints.
 *
 * Conventions:
 *   - Query keys: ['admin', <resource>, ...params]
 *   - Every mutation invalidates the relevant resource root key
 *   - Types flow from @ventin/shared — no duplicated shape definitions
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiFetch } from './api';
import { uploadAttachment } from './attachmentUpload';
import type { ScreenshotResult } from '@/components/screenshot/types';
import type {
  CompanyWebsiteSummary,
  CompanyWebsiteCreateInput,
  CompanyCreateInput,
  CompanyUpdateInput,
  CompanySummary,
  AgreementCreateInput,
  AgreementUpdateInput,
  AgreementSummary,
  UserAdminCreateInput,
  UserAdminUpdateInput,
  UserAdminSummary,
  KnowledgebaseCreateInput,
  KnowledgebaseUpdateInput,
  KnowledgebaseSummary,
  AuditLogFilterInput,
  AuditLogListResponse,
  RoleSchemaType,
  TicketCreateInput,
  TicketUpdateInput,
  TicketSummary,
  TicketListFilter,
  TicketCommentSummary,
  TicketCommentCreateInput,
  TicketAttachmentSummary,
  TimeSessionStartInput,
  TimeSessionAdjustInput,
  TimeSessionWaiveInput,
  TimeSessionSummary,
  MonthlyReportSummary,
} from '@ventin/shared';

// ---------- query key roots ----------

const KEYS = {
  companies: ['admin', 'companies'] as const,
  company: (id: string) => ['admin', 'companies', id] as const,
  companyAgreements: (companyId: string) =>
    ['admin', 'companies', companyId, 'agreements'] as const,
  currentAgreement: (companyId: string) =>
    ['admin', 'companies', companyId, 'agreements', 'current'] as const,
  users: ['admin', 'users'] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  articles: ['admin', 'knowledgebase'] as const,
  article: (slug: string) => ['admin', 'knowledgebase', slug] as const,
  auditLogs: ['admin', 'audit-logs'] as const,
  tickets: ['admin', 'tickets'] as const,
  ticket: (id: string) => ['admin', 'tickets', id] as const,
  ticketComments: (id: string) => ['admin', 'tickets', id, 'comments'] as const,
  ticketAttachments: (id: string) => ['admin', 'tickets', id, 'attachments'] as const,
  sessions: ['admin', 'time-sessions'] as const,
  session: (id: string) => ['admin', 'time-sessions', id] as const,
  liveSession: ['admin', 'time-sessions', 'me', 'live'] as const,
  suggest: (q: string) => ['admin', 'knowledgebase', 'suggest', q] as const,
  reports: ['admin', 'reports'] as const,
  report: (id: string) => ['admin', 'reports', id] as const,
  abusePatterns: ['admin', 'reports', 'abuse'] as const,
} as const;

// ---------- small helpers ----------

/**
 * Serialize a filter object to a `?a=b&c=d` query string. Undefined / null
 * values are omitted; booleans become `1`/absent (so `includeArchived: true`
 * → `?includeArchived=1`, `false` → nothing).
 */
function qs(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (typeof v === 'boolean') {
      if (v) usp.set(k, '1');
      continue;
    }
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ============================================================
// COMPANIES
// ============================================================

export interface CompanyListFilter {
  includeArchived?: boolean;
}

export function useCompanies(
  filter: CompanyListFilter = {},
  options?: Omit<UseQueryOptions<{ companies: CompanySummary[] }>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...KEYS.companies, filter] as const,
    queryFn: () =>
      apiFetch<{ companies: CompanySummary[] }>(
        `/api/admin/companies${qs(filter as Record<string, unknown>)}`,
      ),
    ...options,
  });
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.company(id) : ['admin', 'companies', 'none'],
    queryFn: () => apiFetch<{ company: CompanySummary }>(`/api/admin/companies/${id}`),
    enabled: !!id,
  });
}

export function useCreateCompany(
  options?: UseMutationOptions<{ company: CompanySummary }, Error, CompanyCreateInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<{ company: CompanySummary }>('/api/admin/companies', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.companies }),
    ...options,
  });
}

export function useUpdateCompany(
  options?: UseMutationOptions<
    { company: CompanySummary },
    Error,
    { id: string; patch: CompanyUpdateInput }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) =>
      apiFetch<{ company: CompanySummary }>(`/api/admin/companies/${id}`, {
        method: 'PUT',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.companies }),
    ...options,
  });
}

export function useArchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiFetch<{ company: CompanySummary }>(`/api/admin/companies/${id}/archive`, {
        method: 'POST',
        body: { isArchived: true, ...(reason ? { reason } : {}) },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.companies }),
  });
}

export function useUnarchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ company: CompanySummary }>(`/api/admin/companies/${id}/unarchive`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.companies }),
  });
}

// ============================================================
// AGREEMENTS
// ============================================================

export function useCompanyAgreements(companyId: string | undefined) {
  return useQuery({
    queryKey: companyId
      ? KEYS.companyAgreements(companyId)
      : ['admin', 'companies', 'none', 'agreements'],
    queryFn: () =>
      apiFetch<{ agreements: AgreementSummary[] }>(`/api/admin/companies/${companyId}/agreements`),
    enabled: !!companyId,
  });
}

export function useCurrentAgreement(companyId: string | undefined) {
  return useQuery({
    queryKey: companyId
      ? KEYS.currentAgreement(companyId)
      : ['admin', 'companies', 'none', 'agreements', 'current'],
    queryFn: () =>
      apiFetch<{ agreement: AgreementSummary | null }>(
        `/api/admin/companies/${companyId}/agreements/current`,
      ),
    enabled: !!companyId,
  });
}

export function useCreateAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, input }: { companyId: string; input: AgreementCreateInput }) =>
      apiFetch<{ agreement: AgreementSummary }>(`/api/admin/companies/${companyId}/agreements`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.companyAgreements(vars.companyId) });
      qc.invalidateQueries({ queryKey: KEYS.currentAgreement(vars.companyId) });
    },
  });
}

export function useUpdateAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AgreementUpdateInput }) =>
      apiFetch<{ agreement: AgreementSummary }>(`/api/admin/agreements/${id}`, {
        method: 'PUT',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'companies'] }),
  });
}

export function useDeactivateAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiFetch<{ agreement: AgreementSummary }>(`/api/admin/agreements/${id}/deactivate`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'companies'] }),
  });
}

// ============================================================
// USERS
// ============================================================

export interface UserListFilter {
  role?: RoleSchemaType;
  companyId?: string | null;
  cursor?: string;
  limit?: number;
}

export function useUsers(filter: UserListFilter = {}) {
  return useQuery({
    queryKey: [...KEYS.users, filter] as const,
    queryFn: () =>
      apiFetch<{ users: UserAdminSummary[]; nextCursor: string | null }>(
        `/api/admin/users${qs(filter as Record<string, unknown>)}`,
      ),
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.user(id) : ['admin', 'users', 'none'],
    queryFn: () => apiFetch<{ user: UserAdminSummary }>(`/api/admin/users/${id}`),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserAdminCreateInput) =>
      apiFetch<{ user: UserAdminSummary }>('/api/admin/users', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UserAdminUpdateInput }) =>
      apiFetch<{ user: UserAdminSummary }>(`/api/admin/users/${id}`, {
        method: 'PUT',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users }),
  });
}

export function useReassignUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string | null }) =>
      apiFetch<{ user: UserAdminSummary }>(`/api/admin/users/${id}/reassign`, {
        method: 'POST',
        body: { companyId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ user: UserAdminSummary }>(`/api/admin/users/${id}/deactivate`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users }),
  });
}

// ============================================================
// KNOWLEDGEBASE
// ============================================================

export interface ArticleListFilter {
  tag?: string;
  includeArchived?: boolean;
  limit?: number;
  cursor?: string;
}

export function useArticles(filter: ArticleListFilter = {}) {
  return useQuery({
    queryKey: [...KEYS.articles, filter] as const,
    queryFn: () =>
      apiFetch<{ articles: KnowledgebaseSummary[]; nextCursor: string | null }>(
        `/api/admin/knowledgebase${qs(filter as Record<string, unknown>)}`,
      ),
  });
}

export function useArticle(slug: string | undefined) {
  return useQuery({
    queryKey: slug ? KEYS.article(slug) : ['admin', 'knowledgebase', 'none'],
    queryFn: () => apiFetch<{ article: KnowledgebaseSummary }>(`/api/admin/knowledgebase/${slug}`),
    enabled: !!slug,
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: KnowledgebaseCreateInput) =>
      apiFetch<{ article: KnowledgebaseSummary }>('/api/admin/knowledgebase', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.articles }),
  });
}

export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: KnowledgebaseUpdateInput }) =>
      apiFetch<{ article: KnowledgebaseSummary }>(`/api/admin/knowledgebase/${id}`, {
        method: 'PUT',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.articles }),
  });
}

export function useArchiveArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ article: KnowledgebaseSummary }>(`/api/admin/knowledgebase/${id}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.articles }),
  });
}

export function useUnarchiveArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ article: KnowledgebaseSummary }>(`/api/admin/knowledgebase/${id}/unarchive`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.articles }),
  });
}

// ============================================================
// AUDIT LOG
// ============================================================

export type AuditLogFilter = Partial<AuditLogFilterInput>;

export function useAuditLogs(filter: AuditLogFilter = {}) {
  return useQuery({
    queryKey: [...KEYS.auditLogs, filter] as const,
    queryFn: () =>
      apiFetch<AuditLogListResponse>(
        `/api/admin/audit-logs${qs(filter as Record<string, unknown>)}`,
      ),
  });
}

// ============================================================
// IMPERSONATION
// ============================================================

export interface StartImpersonationInput {
  userId: string;
  reason?: string;
}

export interface StartImpersonationResponse {
  expiresAt: string;
  target: UserAdminSummary;
}

export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartImpersonationInput) =>
      apiFetch<StartImpersonationResponse>('/api/admin/impersonate', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.auditLogs }),
  });
}

export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>('/api/admin/impersonate/stop', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.auditLogs }),
  });
}

// ============================================================
// TICKETS
// ============================================================

export function useTickets(filter?: Partial<TicketListFilter>) {
  return useQuery({
    queryKey: [...KEYS.tickets, filter] as const,
    queryFn: () =>
      apiFetch<{ tickets: TicketSummary[]; nextCursor: string | null }>(
        `/api/tickets${filter ? qs(filter as Record<string, unknown>) : ''}`,
      ),
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: KEYS.ticket(id),
    queryFn: () => apiFetch<{ ticket: TicketSummary }>(`/api/tickets/${id}`),
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TicketCreateInput) =>
      apiFetch<{ ticket: TicketSummary }>('/api/tickets', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tickets }),
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TicketUpdateInput }) =>
      apiFetch<{ ticket: TicketSummary }>(`/api/tickets/${id}`, { method: 'PUT', body: patch }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.tickets });
      qc.invalidateQueries({ queryKey: KEYS.ticket(vars.id) });
    },
  });
}

/**
 * Comment thread for a ticket. The API scopes visibility at the service layer:
 * CLIENT callers never receive `isInternal` notes, so the same hook is safe to
 * use from both admin and portal surfaces.
 */
export function useTicketComments(id: string) {
  return useQuery({
    queryKey: KEYS.ticketComments(id),
    queryFn: () => apiFetch<{ comments: TicketCommentSummary[] }>(`/api/tickets/${id}/comments`),
    enabled: !!id,
  });
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TicketCommentCreateInput }) =>
      apiFetch<{ comment: TicketCommentSummary }>(`/api/tickets/${id}/comments`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: KEYS.ticketComments(vars.id) }),
  });
}

/** Screenshot attachments for a ticket (visibility scoped server-side). */
export function useTicketAttachments(id: string) {
  return useQuery({
    queryKey: KEYS.ticketAttachments(id),
    queryFn: () =>
      apiFetch<{ attachments: TicketAttachmentSummary[] }>(`/api/tickets/${id}/attachments`),
    enabled: !!id,
  });
}

/**
 * Upload one annotated screenshot (two-step raw + JSON). Used for direct
 * attach on the ticket-detail surfaces; the portal new-ticket flow calls
 * {@link uploadAttachment} directly so it can sequence + retry per file.
 */
export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, result }: { id: string; result: ScreenshotResult }) =>
      uploadAttachment(id, result),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: KEYS.ticketAttachments(vars.id) }),
  });
}

// ============================================================
// TIME-SESSIONS
// ============================================================

export function useLiveSession(opts: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: KEYS.liveSession,
    queryFn: () => apiFetch<{ session: TimeSessionSummary | null }>('/api/time-sessions/me/live'),
    refetchInterval: opts.refetchInterval,
  });
}

export function useSessions(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: [...KEYS.sessions, filter] as const,
    queryFn: () =>
      apiFetch<{ sessions: TimeSessionSummary[]; nextCursor: string | null }>(
        `/api/time-sessions${filter ? qs(filter) : ''}`,
      ),
  });
}

export function useStartSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeSessionStartInput) =>
      apiFetch<{ session: TimeSessionSummary }>('/api/time-sessions/start', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.liveSession });
      qc.invalidateQueries({ queryKey: KEYS.sessions });
    },
  });
}

export function usePauseSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ session: TimeSessionSummary }>(`/api/time-sessions/${id}/pause`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.liveSession }),
  });
}

export function useResumeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ session: TimeSessionSummary }>(`/api/time-sessions/${id}/resume`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.liveSession }),
  });
}

export function useStopSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiFetch<{ session: TimeSessionSummary }>(`/api/time-sessions/${id}/stop`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.liveSession });
      qc.invalidateQueries({ queryKey: KEYS.tickets });
    },
  });
}

export function useAdjustSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TimeSessionAdjustInput }) =>
      apiFetch<{ session: TimeSessionSummary }>(`/api/time-sessions/${id}/adjust`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: KEYS.session(vars.id) }),
  });
}

export function useWaiveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TimeSessionWaiveInput }) =>
      apiFetch<{ session: TimeSessionSummary }>(`/api/time-sessions/${id}/waive`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: KEYS.session(vars.id) }),
  });
}

export function useAttachTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, ticketIds }: { sessionId: string; ticketIds: string[] }) =>
      apiFetch<{ ticketIds: string[] }>(`/api/time-sessions/${sessionId}/tickets`, {
        method: 'POST',
        body: { ticketIds },
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.session(vars.sessionId) });
      qc.invalidateQueries({ queryKey: KEYS.tickets });
    },
  });
}

export function useDetachTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, ticketIds }: { sessionId: string; ticketIds: string[] }) => {
      const results = await Promise.all(
        ticketIds.map((tid) =>
          apiFetch<{ ticketIds: string[] }>(`/api/time-sessions/${sessionId}/tickets/${tid}`, {
            method: 'DELETE',
          }),
        ),
      );
      return results[results.length - 1]!;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.session(vars.sessionId) });
      qc.invalidateQueries({ queryKey: KEYS.tickets });
    },
  });
}

// ============================================================
// REPORTS (Phase 4 — ADM-04/05/07, RPT-09)
// ============================================================

export interface AdminReportsFilter {
  companyId?: string;
  month?: string; // 'YYYY-MM'
  billable?: boolean;
  limit?: number;
  cursor?: string;
}

export interface AdminReportBreakdownRow {
  sessionId: string;
  companyId: string;
  companyName: string;
  ticketId: string | null;
  ticketTitle: string | null;
  durationMinutes: number;
  incrementsBilled: number;
  isBillable: boolean;
  isAdjusted: boolean;
  isWaived: boolean;
}

export interface AdminReportsSummary {
  totalBillableIncrements: number;
  totalNonBillableSessions: number;
  totalOverageIncrements: number;
}

export interface AdminReportsResponse {
  summary: AdminReportsSummary;
  breakdown: AdminReportBreakdownRow[];
  reports: MonthlyReportSummary[];
  nextCursor: string | null;
}

export interface AbusePattern {
  companyId: string;
  companyName: string;
  patternType: 'high-adjustment-frequency' | 'unusual-session-length';
  description: string;
}

export function useAdminReports(
  filter: AdminReportsFilter = {},
  options?: Omit<UseQueryOptions<AdminReportsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...KEYS.reports, filter] as const,
    queryFn: () =>
      apiFetch<AdminReportsResponse>(`/api/admin/reports${qs(filter as Record<string, unknown>)}`),
    ...options,
  });
}

export function useAdminAbusePatterns(
  filter: { companyId?: string; month?: string } = {},
  options?: Omit<UseQueryOptions<{ patterns: AbusePattern[] }>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...KEYS.abusePatterns, filter] as const,
    queryFn: () =>
      apiFetch<{ patterns: AbusePattern[] }>(
        `/api/admin/reports/abuse-patterns${qs(filter as Record<string, unknown>)}`,
      ),
    ...options,
  });
}

export function buildAdminReportDownloadUrl(id: string, format: 'pdf' | 'csv'): string {
  return `/api/admin/reports/${encodeURIComponent(id)}/download?format=${format}`;
}

export function buildAdminReportExportUrl(filter: {
  companyId?: string;
  month?: string;
  format: 'pdf' | 'csv';
}): string {
  return `/api/admin/reports/export${qs(filter as Record<string, unknown>)}`;
}

// ============================================================
// KB SUGGEST
// ============================================================

export function useSuggestArticles(
  q: string,
  { enabled, limit }: { enabled?: boolean; limit?: number } = {},
) {
  return useQuery({
    queryKey: KEYS.suggest(q),
    queryFn: () => {
      const params: Record<string, unknown> = { q };
      if (limit !== undefined) params.limit = limit;
      return apiFetch<{ articles: KnowledgebaseSummary[] }>(
        `/api/admin/knowledgebase/suggest${qs(params)}`,
      );
    },
    enabled: q.trim().length > 0 && (enabled ?? true),
    staleTime: 30_000,
  });
}

// --- Company websites (owner spec 2026-06-11) -------------------------------

const websitesKey = (companyId: string) => ['admin', 'companies', companyId, 'websites'] as const;

export function useCompanyWebsites(companyId: string | undefined) {
  return useQuery({
    queryKey: companyId ? websitesKey(companyId) : ['admin', 'companies', 'none', 'websites'],
    queryFn: () =>
      apiFetch<{ websites: CompanyWebsiteSummary[] }>(`/api/admin/companies/${companyId}/websites`),
    enabled: !!companyId,
  });
}

export function useAddCompanyWebsite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, input }: { companyId: string; input: CompanyWebsiteCreateInput }) =>
      apiFetch<{ website: CompanyWebsiteSummary }>(`/api/admin/companies/${companyId}/websites`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_d, { companyId }) => {
      void qc.invalidateQueries({ queryKey: websitesKey(companyId) });
    },
  });
}

export function useRemoveCompanyWebsite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, websiteId }: { companyId: string; websiteId: string }) =>
      apiFetch<void>(`/api/admin/companies/${companyId}/websites/${websiteId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, { companyId }) => {
      void qc.invalidateQueries({ queryKey: websitesKey(companyId) });
    },
  });
}

// --- Operator cache-clear (plan §3.3, ADMIN + SUPPORT) ----------------------

/** Per-cache-layer outcome returned by the MainWP clear-cache action. */
export type CacheLayerStatus = 'cleared' | 'absent' | 'error';
export interface CacheClearLayer {
  layer: string;
  status: CacheLayerStatus;
}
export interface ClearCacheResponse {
  result: { mainwpSiteId: string; layers: CacheClearLayer[] };
  cacheLastClearedAt: string | null;
}

/**
 * Flush a website's caches (Beaver Builder + page/object cache) via the MainWP
 * extension. Idempotent and operator-only; the server maps an unconfigured
 * secret to 503 and an unlinked site to 400, surfaced by the caller. Optional
 * `companyId` lets the mutation refresh that company's website list on success.
 */
export function useClearSiteCache() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ websiteId }: { websiteId: string; companyId?: string }) =>
      apiFetch<ClearCacheResponse>(`/api/admin/websites/${websiteId}/clear-cache`, {
        method: 'POST',
      }),
    onSuccess: (_data, { companyId }) => {
      if (companyId) void qc.invalidateQueries({ queryKey: websitesKey(companyId) });
    },
  });
}
